import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { deriveVariants } from '@/lib/media';
import type { GalleryImage } from '@/types/content';

interface LightboxProps {
  images: GalleryImage[];
  /** Index to open at. Null = closed. */
  openIndex: number | null;
  onClose: () => void;
}

/**
 * Full-screen image viewer for project galleries. Portal-rendered so it sits
 * above the page's stacking context. Self-handles keyboard navigation
 * (Esc / arrow keys) and respects prefers-reduced-motion (no transition).
 *
 * SSR-safe: guards createPortal behind a mount flag so prerender doesn't
 * touch document.body.
 */
export function Lightbox({ images, openIndex, onClose }: LightboxProps) {
  const [mounted, setMounted] = useState(false);
  const [index, setIndex] = useState(openIndex ?? 0);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Sync internal index when opener changes openIndex
  useEffect(() => {
    if (openIndex !== null) setIndex(openIndex);
  }, [openIndex]);

  const isOpen = openIndex !== null;

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  // Keyboard handlers + body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, goPrev, goNext]);

  if (!mounted || !isOpen) return null;

  const image = images[index];
  if (!image) return null;
  const v = deriveVariants(image.src);

  const modal = (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Gallery image ${index + 1} of ${images.length}`}
      onClick={(e) => {
        // Click on backdrop (not on figure) closes
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button
        type="button"
        className="lightbox__close"
        onClick={onClose}
        aria-label="Close gallery"
      >
        <span aria-hidden="true">×</span>
      </button>

      {images.length > 1 ? (
        <>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--prev"
            onClick={goPrev}
            aria-label="Previous image"
          >
            <span aria-hidden="true">←</span>
          </button>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--next"
            onClick={goNext}
            aria-label="Next image"
          >
            <span aria-hidden="true">→</span>
          </button>
        </>
      ) : null}

      <figure className="lightbox__figure">
        <picture>
          {v.avif ? <source srcSet={v.avif} type="image/avif" /> : null}
          {v.webp ? <source srcSet={v.webp} type="image/webp" /> : null}
          <img
            src={v.fallback}
            alt={image.alt}
            className="lightbox__image"
            draggable={false}
          />
        </picture>
        {image.caption ? (
          <figcaption className="lightbox__caption caption">
            <span className="lightbox__counter">
              {index + 1} / {images.length}
            </span>
            {image.caption}
          </figcaption>
        ) : (
          <figcaption className="lightbox__caption caption">
            <span className="lightbox__counter">
              {index + 1} / {images.length}
            </span>
            {image.alt}
          </figcaption>
        )}
      </figure>
    </div>
  );

  return createPortal(modal, document.body);
}
